import { useState } from 'react';
import { AlertTriangle, CheckCircle2, Circle, FileClock, Loader2, Lock } from 'lucide-react';
import { vatReportingApi } from '../../services/vatReportingApi';
import { useVatLang } from './VatLang';
import type {
    AnnualLineValue, AnnualReturnResponse, PeriodStatus, VatCompany,
} from '../../types/vatReporting';

/**
 * offen -> geprueft -> gemeldet for the ANNUAL return, and what each step means.
 *
 * THE SAME LADDER AS THE MONTH, ONE STEP HEAVIER. Filing a month freezes its transactions;
 * filing a YEAR records what the Finanzamt now holds for the whole year - the figures that
 * every later correction is measured against. So the form asks for two things the server
 * cannot know: the ELSTER transfer ticket, and what was actually keyed into the form. The
 * computed side is recomputed SERVER-SIDE at that moment and stored beside it, and the
 * difference between the two becomes a finding rather than being reconciled away.
 *
 * WHY THE FILED FIGURES ARE PREFILLED AND STILL EDITABLE. In the normal case Heike types the
 * Meldewerte this page produced, so prefilling them is the honest default and one click. In
 * the case that matters - she had to enter something else, as the filed dch 2025 Zeile 119
 * shows - the difference has to be recordable, or the page would quietly claim the return says
 * what we computed.
 *
 * REOPENING KEEPS THE FILED RECORD. `setAnnualStatus` is called WITHOUT filedValues, ticket or
 * note, and the server reads that as "leave them alone". Three of the four filed years in the
 * database were transcribed from a PDF; a reopen that erased them would be unrecoverable.
 */

interface Props {
    data: AnnualReturnResponse;
    company: VatCompany;
    year: number;
    canEdit: boolean;
    onChanged: () => void;
    onError: (message: string) => void;
}

const STEPS: { key: PeriodStatus; labelKey: string }[] = [
    { key: 'open', labelKey: 'status.open' },
    { key: 'reviewed', labelKey: 'status.reviewed' },
    { key: 'filed', labelKey: 'status.filed' },
];

const money = (v: number | null | undefined) =>
    v === null || v === undefined
        ? '—'
        : new Intl.NumberFormat('de-CH', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v);

/** What goes into the input: a plain number, no thousands separators, so it round-trips. */
const editable = (v: number | null): string => (v === null ? '' : String(v));

export function AnnualStatusBar({ data, company, year, canEdit, onChanged, onError }: Props) {
    const { t } = useVatLang();
    const stored = data.annualReturn;
    const status: PeriodStatus = stored?.status ?? 'open';
    const backfilled = !!stored?.filedSourceRef;

    const [busy, setBusy] = useState(false);
    const [showFileForm, setShowFileForm] = useState(false);
    const [ticket, setTicket] = useState(stored?.transferTicket ?? '');
    const [note, setNote] = useState(stored?.note ?? '');
    const [filed, setFiled] = useState<Record<string, string>>(() => prefill(data.lines, stored));

    const blockingErrors = data.checks.filter(c => c.status === 'error');

    async function move(next: PeriodStatus, withFiledValues: boolean) {
        setBusy(true);
        try {
            const filedValues = withFiledValues ? parseFiled(filed) : undefined;
            if (withFiledValues && filedValues === null) {
                onError(t('annualStatus.notANumber'));
                return;
            }
            await vatReportingApi.setAnnualStatus({
                company, year, status: next,
                // Only a real filing writes these; a reopen leaves the stored record intact.
                ...(withFiledValues
                    ? { transferTicket: ticket.trim() || null, note: note.trim() || null,
                        filedValues: filedValues ?? {} }
                    : {}),
            });
            setShowFileForm(false);
            onChanged();
        } catch (e) {
            onError(e instanceof Error ? e.message : t('status.changeFailed'));
        } finally {
            setBusy(false);
        }
    }

    return (
        <div className="border rounded-lg bg-white">
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 px-4 py-3">
                <h3 className="text-sm font-semibold text-gray-900">
                    {t('annual.title', { year: data.year })}
                </h3>

                <div className="flex items-center gap-4">
                    {STEPS.map((s, i) => {
                        const reached = STEPS.findIndex(x => x.key === status) >= i;
                        const current = s.key === status;
                        return (
                            <div key={s.key} className="flex items-center gap-1.5">
                                {s.key === 'filed' && reached
                                    ? <Lock className="h-4 w-4 text-green-600" />
                                    : reached
                                        ? <CheckCircle2 className="h-4 w-4 text-green-600" />
                                        : <Circle className="h-4 w-4 text-gray-300" />}
                                <span className={`text-sm ${current ? 'font-semibold text-gray-900' : 'text-gray-500'}`}>
                                    {t(s.labelKey)}
                                </span>
                            </div>
                        );
                    })}
                    {backfilled && (
                        <span className="inline-flex items-center gap-1 rounded-full border border-amber-300
                                         bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-800">
                            <FileClock className="h-3.5 w-3.5" />
                            {t('annualStatus.fromProtocol')}
                        </span>
                    )}
                </div>

                <div className="text-xs text-gray-500 flex-1 min-w-[16rem] space-x-3">
                    <span>{t('annual.monthsFiled', { filed: data.filedMonthCount })}</span>
                    {stored?.filedAt && (
                        <span>{t('annual.filedAt', {
                            date: new Date(stored.filedAt).toLocaleDateString('de-CH'),
                            by: stored.filedBy ?? '—',
                        })}</span>
                    )}
                    {status === 'reviewed' && stored?.reviewedAt && (
                        <span>{t('status.reviewedBy', {
                            date: new Date(stored.reviewedAt).toLocaleDateString('de-CH'),
                            user: stored.reviewedBy ?? '—',
                        })}</span>
                    )}
                    {stored?.transferTicket && (
                        <span>{t('annual.transferTicket')}: <code>{stored.transferTicket}</code></span>
                    )}
                    {status === 'open' && !stored && t('annualStatus.nothingRecorded')}
                </div>

                {canEdit && (
                    <div className="flex items-center gap-2">
                        {busy && <Loader2 className="h-4 w-4 animate-spin text-gray-400" />}
                        {status === 'open' && (
                            <button onClick={() => move('reviewed', false)} disabled={busy}
                                    className="px-3 py-1.5 text-sm border rounded hover:bg-gray-50 disabled:opacity-50">
                                {t('status.markReviewed')}
                            </button>
                        )}
                        {status !== 'filed' && (
                            <button onClick={() => setShowFileForm(v => !v)} disabled={busy}
                                    className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50">
                                {t('annualStatus.markFiled')}
                            </button>
                        )}
                        {status === 'filed' && (
                            <button onClick={() => move('reviewed', false)} disabled={busy}
                                    title={t('annualStatus.reopenHint')}
                                    className="px-3 py-1.5 text-sm border rounded hover:bg-gray-50 disabled:opacity-50">
                                {t('status.reopen')}
                            </button>
                        )}
                    </div>
                )}
            </div>

            <div className="px-4 pb-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
                <span>{t('annual.identity', {
                    taxNumber: data.filingIdentity.taxNumber ?? '—',
                    vatId: data.filingIdentity.vatRegistrationNo ?? '—',
                    office: data.filingIdentity.taxOffice ?? '—',
                })}</span>
                {stored?.note && <span className="text-gray-600">{stored.note}</span>}
            </div>

            {backfilled && (
                <div className="border-t border-amber-200 bg-amber-50 px-4 py-2 text-xs text-amber-900">
                    {t('annual.backfilled', { source: stored?.filedSourceRef ?? '' })}
                </div>
            )}

            {showFileForm && canEdit && (
                <div className="border-t px-4 py-3 bg-gray-50 space-y-3">
                    {blockingErrors.length > 0 && (
                        <div className="flex items-start gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2">
                            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                            <span>{t('status.stillFailing', { count: blockingErrors.length })}</span>
                        </div>
                    )}
                    <p className="text-sm text-gray-600">
                        {t('annualStatus.fileExplainer', { year, filed: data.filedMonthCount })}
                    </p>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <label className="text-sm">
                            <span className="block text-gray-600 mb-1">{t('annualStatus.ticket')}</span>
                            <input value={ticket} onChange={e => setTicket(e.target.value)}
                                   placeholder="ep2326qh6v8r0m23e1fv6do1phyzc2wf"
                                   className="w-full border rounded px-2 py-1 font-mono text-xs" />
                        </label>
                        <label className="text-sm">
                            <span className="block text-gray-600 mb-1">{t('status.note')}</span>
                            <input value={note} onChange={e => setNote(e.target.value)}
                                   className="w-full border rounded px-2 py-1 text-xs" />
                        </label>
                    </div>

                    <div className="border rounded bg-white overflow-hidden">
                        <div className="px-3 py-2 border-b bg-white flex items-center justify-between">
                            <span className="text-xs font-semibold text-gray-700">
                                {t('annualStatus.filedValuesTitle')}
                            </span>
                            <button onClick={() => setFiled(prefill(data.lines, null))}
                                    className="text-xs text-blue-600 hover:text-blue-800">
                                {t('annualStatus.takeComputed')}
                            </button>
                        </div>
                        <div className="max-h-[40vh] overflow-auto">
                            <table className="w-full text-xs tabular-nums">
                                <tbody className="divide-y divide-gray-100">
                                    {data.lines.map(line => {
                                        const entered = filed[line.key] ?? '';
                                        const diff = differenceOf(entered, line.reportedValue);
                                        return (
                                            <tr key={line.key}>
                                                <td className="px-3 py-1 text-gray-500 whitespace-nowrap w-16">
                                                    {line.formLineNo !== null ? `Z. ${line.formLineNo}` : '—'}
                                                </td>
                                                <td className="px-3 py-1 text-gray-800">{line.label}</td>
                                                <td className="px-3 py-1 text-right text-gray-500 whitespace-nowrap w-32">
                                                    {money(line.reportedValue)}
                                                </td>
                                                <td className="px-3 py-1 w-36">
                                                    <input value={entered}
                                                           inputMode="decimal"
                                                           onChange={e => setFiled(f => ({ ...f, [line.key]: e.target.value }))}
                                                           className="w-full border rounded px-2 py-0.5 text-right tabular-nums" />
                                                </td>
                                                <td className={`px-3 py-1 text-right whitespace-nowrap w-28 ${
                                                    diff === null ? 'text-gray-300'
                                                        : Math.abs(diff) < 0.005 ? 'text-green-600'
                                                            : 'font-medium text-amber-700'}`}>
                                                    {diff === null ? '·' : Math.abs(diff) < 0.005 ? '✓' : money(diff)}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <div className="flex gap-2">
                        <button onClick={() => move('filed', true)} disabled={busy}
                                className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50">
                            {t('annualStatus.recordFiling')}
                        </button>
                        <button onClick={() => setShowFileForm(false)}
                                className="px-3 py-1.5 text-sm border rounded hover:bg-white">
                            {t('status.cancel')}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

/** Stored filed figures win over the computation - re-filing a year starts from the record. */
function prefill(
    lines: AnnualLineValue[],
    stored: AnnualReturnResponse['annualReturn'] | null,
): Record<string, string> {
    const byKey = new Map((stored?.values ?? []).map(v => [v.lineKey, v.filedValue]));
    const out: Record<string, string> = {};
    for (const l of lines) {
        const fromStore = byKey.get(l.key);
        out[l.key] = editable(fromStore !== undefined && fromStore !== null ? fromStore : l.reportedValue);
    }
    return out;
}

/** Null when any field is not a number - a typo must stop the write, not become a zero. */
function parseFiled(filed: Record<string, string>): Record<string, number> | null {
    const out: Record<string, number> = {};
    for (const [key, raw] of Object.entries(filed)) {
        const text = raw.trim().replace(/'/g, '').replace(',', '.');
        if (text === '') continue;
        const value = Number(text);
        if (!Number.isFinite(value)) return null;
        out[key] = Math.round(value * 100) / 100;
    }
    return out;
}

function differenceOf(entered: string, computed: number | null): number | null {
    const text = entered.trim().replace(/'/g, '').replace(',', '.');
    if (text === '' || computed === null) return null;
    const value = Number(text);
    if (!Number.isFinite(value)) return null;
    return Math.round((value - computed) * 100) / 100;
}
